import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery } from 'react-query'
import {
  getOverlayStatus,
  showOverlayCard,
  hideOverlayCard,
} from '../../../Apis/overlays/streamCards'
import QueryKeys from '../../../utils/constants/queryKeys'

// The feeder draws a card in the background and can take seconds, or never manage it.
export const RENDER_TIMEOUT_MS = 15_000
const STATUS_POLL_MS = 1000

/**
 * The card on air and the one still being rendered. Only one card is ever on screen, so the
 * streamer needs no memory of the others and neither do we: the choice is held locally and the
 * server answers first. A chosen card stays `pending` until the streamer's status reports a
 * frame for it; after RENDER_TIMEOUT_MS without one it is hidden and handed to `onFailed`.
 */
const useOverlayCard = (orgId, streamId, { onFailed } = {}) => {
  const [activeCard, setActiveCard] = useState(null)
  const [pending, setPending] = useState(null)

  // An operator can walk in on a stream that is already showing something — a colleague put it
  // up, or this is a reload of their own session. Ask the streamer what is on air so the rack
  // opens on the truth instead of looking idle.
  const { data: status, dataUpdatedAt } = useQuery(
    [QueryKeys.GET_OVERLAY_STATUS, orgId, streamId],
    () => getOverlayStatus(orgId, streamId),
    {
      enabled: Boolean(orgId && streamId),
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      // No stream, no card: the rack simply opens empty rather than retrying at a dead pod.
      retry: false,
      // Polled only while a card is rendering.
      refetchInterval: pending ? STATUS_POLL_MS : false,
    },
  )

  // Seeded once and only once. From the first answer on, the operator's own clicks are the
  // truth, and a later read landing on top of them would fight the person using the panel.
  const seededFromStream = useRef(false)
  useEffect(() => {
    if (seededFromStream.current || !status) return
    seededFromStream.current = true
    if (!status.activeCard) return
    // activeTeam is absent until the streamer reports the side a team card was opened with;
    // until then such a card comes back live with neither team lit, which is honest.
    setActiveCard({ type: status.activeCard, team: status.activeTeam ?? null })
  }, [status])

  const cardMutation = useMutation((next) =>
    next ? showOverlayCard(orgId, streamId, next) : hideOverlayCard(orgId, streamId),
  )

  const changeCard = (next) => {
    const previous = activeCard
    const card = next && {
      type: next.overlayType,
      team: next.team ?? null,
      quarter: next.quarter ?? null,
    }
    setActiveCard(card)
    setPending(null)
    cardMutation.mutate(next, {
      onError: () => setActiveCard(previous),
      // The streamer has the card from here; the frame is still to come.
      onSuccess: () => card && setPending({ ...card, since: Date.now() }),
    })
  }

  // A status read after the request, naming this card with a frame, ends the wait. A streamer
  // that does not report its card feed predates the loader: there is nothing to wait for.
  useEffect(() => {
    if (!pending || !status || dataUpdatedAt < pending.since) return
    if (
      status.cardFeed === undefined ||
      (status.activeCard === pending.type && status.cardLastFrameAt)
    ) {
      setPending(null)
    }
  }, [status, dataUpdatedAt, pending])

  const latest = useRef({ cardMutation, onFailed })
  latest.current = { cardMutation, onFailed }
  useEffect(() => {
    if (!pending) return undefined
    const timer = setTimeout(() => {
      // Hidden rather than left to appear on air a minute later, once the operator has moved on.
      setPending(null)
      setActiveCard(null)
      latest.current.cardMutation.mutate(null)
      if (latest.current.onFailed) latest.current.onFailed(pending)
    }, RENDER_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [pending])

  return { activeCard, pending, changeCard, busy: cardMutation.isLoading }
}

export default useOverlayCard
